import { useEffect, useState } from "react";
import { Mail, Plus, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { startCheckout } from "../lib/checkout";
import { teamCopy } from "../lib/teamCopy";
import { copyToClipboard } from "../lib/copyToClipboard";
import {
  ChalkCard, ChalkIllustrationLines, DarkHeader, EmptyState, WhiteHeader,
} from "../components/chalk/primitives";
import CreateTeamSheet from "../components/chalk/CreateTeamSheet";
import AddAthleteSheet from "../components/chalk/AddAthleteSheet";
import EditAthleteSheet from "../components/chalk/EditAthleteSheet";
import TeamLockBanner from "../components/chalk/TeamLockBanner";
import JoinRequestsCard from "../components/chalk/JoinRequestsCard";
import MergeProfilesCard from "../components/chalk/MergeProfilesCard";
import InviteParentsCard from "../components/chalk/InviteParentsCard";
import TeamStaffCard from "../components/chalk/TeamStaffCard";
import { BottomSheet as InviteSheet } from "../components/chalk/primitives";

const STATUS_PILLS = {
  not_sent:  { label: "Not sent", bg: "rgba(0,0,0,0.06)", fg: "var(--chalk-muted)" },
  sent:      { label: "Invite sent", bg: "rgba(46,125,82,0.10)", fg: "var(--green-mid)" },
  accepted:  { label: "Joined", bg: "rgba(46,125,82,0.18)", fg: "#1F5C3C" },
  expired:   { label: "Expired", bg: "rgba(176,32,32,0.10)", fg: "#B02020" },
};

export default function Roster() {
  const { user } = useAuth();
  const { activeTeam, refresh: refreshTeams } = useTeam();
  const [inviteOpen, setInviteOpen] = useState(false);
  const copy = teamCopy(activeTeam);
  const [athletes, setAthletes] = useState([]);
  const [invitesByAthlete, setInvitesByAthlete] = useState({});
  const [teamSheetOpen, setTeamSheetOpen] = useState(false);
  const [athleteSheetOpen, setAthleteSheetOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [resending, setResending] = useState(null);

  const isStaffSide = user.role !== "guardian";
  const canEdit = isStaffSide && (!activeTeam?.permissions || activeTeam.is_owner || !!activeTeam.permissions.MANAGE_ROSTER);
  const isLocked = !!(activeTeam && activeTeam.is_locked);

  function tryAddAthlete() {
    if (isLocked) {
      startCheckout({
        tier: "chalkboard",
        teamId: activeTeam.id,
        context: { feature_name: "MULTI_TEAM", source_screen: "roster_fab", current_tier: "pickup" },
      }).catch((e) => toast.error(e.message || "Couldn't open checkout."));
      return;
    }
    setAthleteSheetOpen(true);
  }

  async function reloadRoster() {
    if (!activeTeam) { setAthletes([]); setInvitesByAthlete({}); return; }
    try {
      const data = await api.listRoster(activeTeam.id);
      setAthletes(data.athletes || []);
    } catch { /* ignore */ }
    if (canEdit) {
      try {
        const inv = await api.listRosterInvites(activeTeam.id);
        const map = {};
        for (const i of inv.invites || []) map[i.athlete_id] = i;
        setInvitesByAthlete(map);
      } catch { /* ignore */ }
    }
  }

  useEffect(() => { reloadRoster(); /* eslint-disable-next-line */ }, [activeTeam]);

  async function resend(athlete) {
    const invite = invitesByAthlete[athlete.id];
    if (!invite) {
      toast.error("No invite on file. Add their email from the player.");
      return;
    }
    setResending(invite.id);
    try {
      await api.resendRosterInvite(activeTeam.id, invite.id);
      toast.success(`Invite re-sent to ${invite.email}.`);
      await reloadRoster();
    } catch (e) {
      toast.error(e.message || "Couldn't resend.");
    } finally {
      setResending(null);
    }
  }

  const Header = user.role === "guardian" ? WhiteHeader : DarkHeader;

  return (
    <>
      <Header eyebrow="The Roster" title="The Roster" subtitle={activeTeam ? activeTeam.name : ""} />
      <div style={{ padding: "0 18px 110px" }}>
        <TeamLockBanner team={activeTeam} sourceScreen="roster" />

        {/* Coach-only: pending join requests + the team's join code. */}
        {canEdit && activeTeam && activeTeam.is_owner && !isLocked && (
          <>
            <JoinRequestsCard teamId={activeTeam.id} teamName={activeTeam.name} />
            <MergeProfilesCard teamId={activeTeam.id} onMerged={() => reloadRoster()} />
            {activeTeam.join_code && activeTeam.is_owner && (
              <button type="button" data-testid="roster-invite-parents-btn" onClick={() => setInviteOpen(true)}
                style={{ width: "100%", marginBottom: 10, padding: "11px", borderRadius: 999, border: "none", cursor: "pointer",
                  background: "var(--chalk-board)", color: "#F5F0E8", fontSize: 14 }}>
                Invite parents
              </button>
            )}
            <InviteSheet isOpen={inviteOpen} title="Invite your parents" onDismiss={() => setInviteOpen(false)}>
              <InviteParentsCard coach={user.first_name} team={activeTeam.name} joinCode={activeTeam.join_code} teamId={activeTeam.id} />
            </InviteSheet>
            {activeTeam.join_code && (
              <ChalkCard
                data-testid="team-join-code-card"
                style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em", color: "var(--chalk-muted)", fontWeight: 700 }}>
                    Team join code
                  </div>
                  <div style={{
                    marginTop: 4,
                    fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)",
                    fontSize: 24, letterSpacing: "0.18em", color: "var(--chalk-board)",
                  }}>
                    {activeTeam.join_code}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
                    Share with a {copy.inviteeSingularLower} so they can join from sign-up.
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="team-join-code-copy"
                  onClick={async () => {
                    const ok = await copyToClipboard(activeTeam.join_code);
                    if (ok) toast.success("Code copied.");
                    else toast.error("Copy failed — long-press to select.");
                  }}
                  style={{
                    padding: "9px 14px", borderRadius: 8,
                    background: "var(--chalk-board)", color: "#fff", border: "none",
                    fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12,
                    letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                  }}
                >
                  Copy
                </button>
              </ChalkCard>
            )}
          </>
        )}

        {isStaffSide && activeTeam && <TeamStaffCard key={activeTeam.id} teamId={activeTeam.id} />}

        {!activeTeam && canEdit ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Create your first team."
            body="A team unlocks the Playbook, Chalk Talk, and roster. Two minutes to set up."
            ctaLabel="Create a team"
            onCta={() => setTeamSheetOpen(true)}
          />
        ) : !activeTeam ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="No team yet."
            body="Once a coach links you, the roster shows here."
          />
        ) : athletes.length === 0 ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="No squad yet."
            body={canEdit
              ? `Add your first player — we'll email the ${copy.inviteeSingularLower} a join link.`
              : "Players will show up here as the coach adds them."}
            ctaLabel={canEdit ? "Add a player" : null}
            onCta={canEdit ? tryAddAthlete : undefined}
          />
        ) : (
          athletes.map((a) => {
            const invite = invitesByAthlete[a.id];
            const status = invite ? invite.status : (a.guardians.length > 0 ? "accepted" : "not_sent");
            const pill = STATUS_PILLS[status] || STATUS_PILLS.not_sent;
            const showResend = canEdit && invite && status !== "accepted";
            return (
              <ChalkCard
                key={a.id}
                data-testid={`athlete-${a.id}`}
                onClick={canEdit && !isLocked ? () => setEditingAthlete(a) : undefined}
                style={canEdit && !isLocked ? { cursor: "pointer" } : undefined}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>
                      {a.first_name} {a.last_name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
                      {a.jersey ? `#${a.jersey}` : "—"}
                      {" · "}
                      {a.guardians.length} {a.guardians.length === 1 ? copy.inviteeSingularLower : copy.inviteePluralLower}
                    </div>
                  </div>
                  {canEdit && (
                    <span
                      data-testid={`athlete-${a.id}-status`}
                      style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        background: pill.bg, color: pill.fg,
                        padding: "4px 8px", borderRadius: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pill.label}
                    </span>
                  )}
                  {showResend && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); resend(a); }}
                      disabled={resending === invite.id}
                      data-testid={`athlete-${a.id}-resend`}
                      aria-label="Resend invite"
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 32, height: 32, borderRadius: 8,
                        background: "transparent", border: "0.5px solid var(--chalk-border)",
                        color: "var(--chalk-muted)",
                        cursor: resending === invite.id ? "wait" : "pointer",
                      }}
                    >
                      {status === "expired"
                        ? <Mail size={14} aria-hidden="true" />
                        : <RefreshCw size={14} aria-hidden="true" />}
                    </button>
                  )}
                </div>
              </ChalkCard>
            );
          })
        )}
      </div>

      {canEdit && (
        <button
          type="button"
          data-testid="roster-fab"
          onClick={() => activeTeam ? tryAddAthlete() : setTeamSheetOpen(true)}
          aria-label={activeTeam ? "Add a player" : "Create a team"}
          style={{
            position: "fixed", right: 18, bottom: 90,
            width: 56, height: 56, borderRadius: 28,
            background: "var(--green-mid)", color: "#fff", border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 12px 28px rgba(46,125,82,0.32)", cursor: "pointer",
            zIndex: 50,
          }}
        >
          {activeTeam ? <UserPlus size={24} aria-hidden="true" /> : <Plus size={26} aria-hidden="true" />}
        </button>
      )}

      <CreateTeamSheet
        isOpen={teamSheetOpen}
        onDismiss={() => setTeamSheetOpen(false)}
        onCreated={async () => { await refreshTeams(); }}
      />
      <AddAthleteSheet
        isOpen={athleteSheetOpen}
        onDismiss={() => setAthleteSheetOpen(false)}
        team={activeTeam}
        teamId={activeTeam?.id}
        onCreated={() => reloadRoster()}
      />
      <EditAthleteSheet
        isOpen={!!editingAthlete}
        team={activeTeam}
        athlete={editingAthlete}
        onDismiss={() => setEditingAthlete(null)}
        onSaved={() => reloadRoster()}
        onRemoved={() => reloadRoster()}
      />
    </>
  );
}
